import { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  Avatar,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Divider,
  IconButton,
  Tooltip,
  Slider,
} from '@mui/material'
import {
  Security,
  CheckCircle,
  Warning,
  Fingerprint,
  Shield,
  ChildCare,
  PersonAdd,
  Delete,
  Edit,
  Info,
  ContentCopy,
  SupervisedUserCircle,
  Cancel,
} from '@mui/icons-material'
import { settingsApi } from '@api/endpoints/settings'
import type {
  MfaStatusDto,
  MfaSetupDto,
  MfaBackupCodesDto,
  UserPrivacySettingsDto,
  ManagedChildAccountDto,
  ParentAccountDto,
  PendingParentalLinkDto,
  PendingOutboundLinkDto,
} from '@types/settings'
import { ErrorDialog } from '@components/ErrorDialog'
import { getSaveFriendlyError, type FriendlyError } from '@utils/errorHandling'
import { ThemedQRCode } from '@components/ThemedQRCode'

export function SecuritySection() {
  // MFA State
  const [mfaStatus, setMfaStatus] = useState<MfaStatusDto | null>(null)
  const [setupData, setSetupData] = useState<MfaSetupDto | null>(null)
  const [backupCodes, setBackupCodes] = useState<MfaBackupCodesDto | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSettingUp, setIsSettingUp] = useState(false)
  const [isResettingPassword, setIsResettingPassword] = useState(false)
  const [showSetupWizard, setShowSetupWizard] = useState(false)
  const [showDisableDialog, setShowDisableDialog] = useState(false)
  const [showBackupCodes, setShowBackupCodes] = useState(false)
  const [activeStep, setActiveStep] = useState(0)
  const [verificationCode, setVerificationCode] = useState('')
  const [disableCode, setDisableCode] = useState('')

  // Privacy Settings State
  const [privacySettings, setPrivacySettings] = useState<UserPrivacySettingsDto | null>(null)
  const [chatAgeEnabled, setChatAgeEnabled] = useState(false)
  const [ageRange, setAgeRange] = useState<number[]>([18, 65])
  const [isSavingPrivacy, setIsSavingPrivacy] = useState(false)

  // Parental Controls State
  const [managedChildren, setManagedChildren] = useState<ManagedChildAccountDto[]>([])
  const [parentAccounts, setParentAccounts] = useState<ParentAccountDto[]>([])
  const [pendingRequests, setPendingRequests] = useState<PendingParentalLinkDto[]>([])
  const [pendingOutboundRequests, setPendingOutboundRequests] = useState<PendingOutboundLinkDto[]>([])
  const [showLinkChildDialog, setShowLinkChildDialog] = useState(false)
  const [showConfirmLinkDialog, setShowConfirmLinkDialog] = useState(false)
  const [showEditChildDialog, setShowEditChildDialog] = useState(false)
  const [showUnlinkDialog, setShowUnlinkDialog] = useState(false)
  const [childEmail, setChildEmail] = useState('')
  const [linkVerificationCode, setLinkVerificationCode] = useState('')
  const [selectedChild, setSelectedChild] = useState<ManagedChildAccountDto | null>(null)
  const [unlinkTarget, setUnlinkTarget] = useState<{ linkId: string; name: string; type: 'child' | 'parent' } | null>(null)
  const [isProcessingParental, setIsProcessingParental] = useState(false)

  // Child settings editing state
  const [editChildAgeEnabled, setEditChildAgeEnabled] = useState(false)
  const [editChildAgeRange, setEditChildAgeRange] = useState<number[]>([18, 65])

  // Common State
  const [error, setError] = useState<FriendlyError | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  const setupSteps = ['Download App', 'Scan QR Code', 'Verify Code', 'Save Backup Codes']

  // Load all data
  useEffect(() => {
    loadAllData()
  }, [])

  const loadAllData = async () => {
    try {
      setIsLoading(true)
      const [mfa, privacy, children, parents, pending, pendingOutbound] = await Promise.all([
        settingsApi.getMfaStatus(),
        settingsApi.getPrivacySettings().catch(() => null),
        settingsApi.getManagedChildren().catch(() => []),
        settingsApi.getParentAccounts().catch(() => []),
        settingsApi.getPendingLinkRequests().catch(() => []),
        settingsApi.getPendingOutboundRequests().catch(() => []),
      ])

      setMfaStatus(mfa)

      if (privacy) {
        setPrivacySettings(privacy)
        setChatAgeEnabled(privacy.chatAgeRestrictionEnabled)
        setAgeRange([
          privacy.chatMinAgeRestriction ?? 18,
          privacy.chatMaxAgeRestriction ?? 65,
        ])
      }

      setManagedChildren(children)
      setParentAccounts(parents)
      setPendingRequests(pending)
      setPendingOutboundRequests(pendingOutbound)
    } catch (err) {
      setError(getSaveFriendlyError(err))
    } finally {
      setIsLoading(false)
    }
  }

  // MFA Handlers
  const handleStartSetup = async () => {
    try {
      setIsSettingUp(true)
      setError(null)
      const setup = await settingsApi.setupMfa()
      setSetupData(setup)
      setShowSetupWizard(true)
      setActiveStep(0)
    } catch (err) {
      setError(getSaveFriendlyError(err))
    } finally {
      setIsSettingUp(false)
    }
  }

  const handleNext = () => setActiveStep((prev) => prev + 1)
  const handleBack = () => setActiveStep((prev) => prev - 1)

  const handleVerifyAndEnable = async () => {
    try {
      setIsSettingUp(true)
      setError(null)
      await settingsApi.enableMfa({ verificationCode })
      const codes = await settingsApi.regenerateMfaBackupCodes()
      setBackupCodes(codes)
      setSuccessMessage('Multi-factor authentication enabled successfully!')
      handleNext()
      await loadAllData()
    } catch (err) {
      setError(getSaveFriendlyError(err))
    } finally {
      setIsSettingUp(false)
    }
  }

  const handleFinishSetup = () => {
    setShowSetupWizard(false)
    setVerificationCode('')
    setActiveStep(0)
  }

  const handleDisableMfa = async () => {
    try {
      setIsSettingUp(true)
      setError(null)
      await settingsApi.disableMfa({ code: disableCode })
      setSuccessMessage('Multi-factor authentication disabled.')
      setShowDisableDialog(false)
      setDisableCode('')
      await loadAllData()
    } catch (err) {
      setError(getSaveFriendlyError(err))
    } finally {
      setIsSettingUp(false)
    }
  }

  const handleRegenerateBackupCodes = async () => {
    try {
      setIsSettingUp(true)
      setError(null)
      const codes = await settingsApi.regenerateMfaBackupCodes()
      setBackupCodes(codes)
      setShowBackupCodes(true)
      setSuccessMessage('Backup codes regenerated successfully!')
      await loadAllData()
    } catch (err) {
      setError(getSaveFriendlyError(err))
    } finally {
      setIsSettingUp(false)
    }
  }

  const handlePasswordReset = async () => {
    try {
      setIsResettingPassword(true)
      setError(null)
      setSuccessMessage(null)
      const profile = await settingsApi.getUserProfile()
      const response = await settingsApi.requestPasswordReset({ email: profile.email })
      if (response.success) {
        setSuccessMessage('Password reset email sent! Please check your inbox.')
      } else {
        throw new Error(response.message || 'Failed to send reset email')
      }
    } catch (err) {
      setError(getSaveFriendlyError(err))
    } finally {
      setIsResettingPassword(false)
    }
  }

  // Privacy Settings Handlers
  const handleSavePrivacySettings = async () => {
    try {
      setIsSavingPrivacy(true)
      setError(null)

      await settingsApi.updateChatAgeRestriction({
        enabled: chatAgeEnabled,
        minAge: ageRange[0],
        maxAge: ageRange[1],
      })

      setSuccessMessage('Chat age restrictions updated successfully!')
      await loadAllData()
    } catch (err) {
      setError(getSaveFriendlyError(err))
    } finally {
      setIsSavingPrivacy(false)
    }
  }

  const handleAgeRangeChange = (_event: Event, newValue: number | number[]) => {
    setAgeRange(newValue as number[])
  }

  const handleEditChildAgeRangeChange = (_event: Event, newValue: number | number[]) => {
    setEditChildAgeRange(newValue as number[])
  }

  // Parental Controls Handlers
  const handleLinkChild = async () => {
    try {
      setIsProcessingParental(true)
      setError(null)

      const response = await settingsApi.createParentalLink({ childIdentifier: childEmail })
      setSuccessMessage(`Link request sent to ${response.childDisplayName || childEmail}! They will need to confirm the request in their settings.`)
      setShowLinkChildDialog(false)
      setChildEmail('')
      await loadAllData()
    } catch (err) {
      setError(getSaveFriendlyError(err))
    } finally {
      setIsProcessingParental(false)
    }
  }

  const handleConfirmLink = async () => {
    try {
      setIsProcessingParental(true)
      setError(null)

      await settingsApi.confirmParentalLink({ verificationCode: linkVerificationCode })
      setSuccessMessage('Parental link confirmed successfully!')
      setShowConfirmLinkDialog(false)
      setLinkVerificationCode('')
      await loadAllData()
    } catch (err) {
      setError(getSaveFriendlyError(err))
    } finally {
      setIsProcessingParental(false)
    }
  }

  const handleUnlink = async () => {
    if (!unlinkTarget) return

    try {
      setIsProcessingParental(true)
      setError(null)

      await settingsApi.revokeParentalLink(unlinkTarget.linkId)
      setSuccessMessage(`${unlinkTarget.name} has been unlinked.`)
      setShowUnlinkDialog(false)
      setUnlinkTarget(null)
      await loadAllData()
    } catch (err) {
      setError(getSaveFriendlyError(err))
    } finally {
      setIsProcessingParental(false)
    }
  }

  const handleCancelPendingRequest = async (linkId: string, childName: string) => {
    try {
      setIsProcessingParental(true)
      setError(null)

      await settingsApi.cancelPendingRequest(linkId)
      setSuccessMessage(`Link request to ${childName} has been cancelled.`)
      await loadAllData()
    } catch (err) {
      setError(getSaveFriendlyError(err))
    } finally {
      setIsProcessingParental(false)
    }
  }

  const handleDeclineRequest = async (linkId: string, parentName: string) => {
    try {
      setIsProcessingParental(true)
      setError(null)

      await settingsApi.declineParentalLink(linkId)
      setSuccessMessage(`Link request from ${parentName} has been declined.`)
      await loadAllData()
    } catch (err) {
      setError(getSaveFriendlyError(err))
    } finally {
      setIsProcessingParental(false)
    }
  }

  const handleOpenEditChild = (child: ManagedChildAccountDto) => {
    setSelectedChild(child)
    setEditChildAgeEnabled(child.privacySettings.chatAgeRestrictionEnabled)
    setEditChildAgeRange([
      child.privacySettings.chatMinAgeRestriction ?? 18,
      child.privacySettings.chatMaxAgeRestriction ?? 65,
    ])
    setShowEditChildDialog(true)
  }

  const handleSaveChildSettings = async () => {
    if (!selectedChild) return

    try {
      setIsProcessingParental(true)
      setError(null)

      await settingsApi.updateChildPrivacySettings({
        childUserId: selectedChild.childUserId,
        chatAgeRestrictionEnabled: editChildAgeEnabled,
        chatMinAgeRestriction: editChildAgeRange[0],
        chatMaxAgeRestriction: editChildAgeRange[1],
      })

      setSuccessMessage(`Privacy settings updated for ${selectedChild.childDisplayName || 'child account'}.`)
      setShowEditChildDialog(false)
      setSelectedChild(null)
      await loadAllData()
    } catch (err) {
      setError(getSaveFriendlyError(err))
    } finally {
      setIsProcessingParental(false)
    }
  }

  const copyToClipboard = async (text: string) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text)
      } else {
        // Fallback for insecure contexts (non-HTTPS)
        const textArea = document.createElement('textarea')
        textArea.value = text
        textArea.style.position = 'fixed'
        textArea.style.left = '-999999px'
        textArea.style.top = '-999999px'
        document.body.appendChild(textArea)
        textArea.focus()
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
      }
      setSuccessMessage('Verification code copied to clipboard!')
    } catch (err) {
      setError(getSaveFriendlyError(err))
    }
  }

  const formatAge = (ageInMonths: number | null) => {
    if (ageInMonths === null) return 'Unknown'
    const years = Math.floor(ageInMonths / 12)
    return `${years} years old`
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  const isParentManaged = privacySettings?.isParentManaged ?? false

  return (
    <Box>
      {/* Error Dialog */}
      {error && (
        <ErrorDialog
          open={true}
          title={error.title}
          message={error.message}
          details={error.details}
          onClose={() => setError(null)}
        />
      )}

      {/* Success Message */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* MFA Status */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, border: '1px solid', borderColor: 'divider' }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box display="flex" alignItems="center" gap={2}>
            <Security sx={{ fontSize: 40, color: mfaStatus?.isEnabled ? 'success.main' : 'text.secondary' }} />
            <Box>
              <Typography variant="h6">
                Multi-Factor Authentication
                {mfaStatus?.isRequired && (
                  <Chip label="Required by Admin" color="warning" size="small" sx={{ ml: 1 }} />
                )}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {mfaStatus?.isEnabled
                  ? 'Your account is protected with 2FA'
                  : 'Add an extra layer of security to your account'}
              </Typography>
            </Box>
          </Box>
          {mfaStatus?.isEnabled ? (
            <CheckCircle sx={{ color: 'success.main', fontSize: 32 }} />
          ) : (
            <Warning sx={{ color: 'warning.main', fontSize: 32 }} />
          )}
        </Box>

        {mfaStatus?.isEnabled && (
          <Box mt={2}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Setup Date: {new Date(mfaStatus.setupDate!).toLocaleDateString()}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Backup Codes Remaining: {mfaStatus.backupCodesRemaining}
            </Typography>
          </Box>
        )}

        <Box mt={3} display="flex" gap={2}>
          {!mfaStatus?.isEnabled ? (
            <Button
              variant="contained"
              onClick={handleStartSetup}
              disabled={isSettingUp}
              startIcon={isSettingUp ? <CircularProgress size={16} /> : <Fingerprint />}
            >
              {isSettingUp ? 'Setting up...' : 'Enable 2FA'}
            </Button>
          ) : (
            <>
              <Button variant="outlined" onClick={handleRegenerateBackupCodes} disabled={isSettingUp}>
                Regenerate Backup Codes
              </Button>
              {!mfaStatus.isRequired && (
                <Button variant="outlined" color="error" onClick={() => setShowDisableDialog(true)}>
                  Disable 2FA
                </Button>
              )}
            </>
          )}
        </Box>
      </Paper>

      {/* Password Reset */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6" gutterBottom>
          Password
        </Typography>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="body1">Reset your password</Typography>
            <Typography variant="body2" color="text.secondary">
              Click to reset your password. We'll send you an email with instructions.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            onClick={handlePasswordReset}
            disabled={isResettingPassword}
            startIcon={isResettingPassword ? <CircularProgress size={16} /> : null}
          >
            {isResettingPassword ? 'Sending...' : 'Reset Password'}
          </Button>
        </Box>
      </Paper>

      {/* Chat Age Restrictions */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, border: '1px solid', borderColor: 'divider' }}>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <Shield sx={{ fontSize: 28, color: 'primary.main' }} />
          <Typography variant="h6">Chat Age Restrictions</Typography>
          <Tooltip title="Restrict who can start direct message conversations with you based on their age">
            <Info sx={{ fontSize: 18, color: 'text.secondary', cursor: 'help' }} />
          </Tooltip>
        </Box>

        {isParentManaged && (
          <Alert severity="info" sx={{ mb: 3 }}>
            These settings are managed by your parent/guardian. Contact them to make changes.
          </Alert>
        )}

        <FormControlLabel
          control={
            <Switch
              checked={chatAgeEnabled}
              onChange={(e) => setChatAgeEnabled(e.target.checked)}
              disabled={isParentManaged || isSavingPrivacy}
            />
          }
          label="Restrict who can start chats with me based on age"
        />

        {chatAgeEnabled && (
          <Box mt={3} px={2}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Allow chats from users aged {ageRange[0]} to {ageRange[1]} years
            </Typography>
            <Slider
              value={ageRange}
              onChange={handleAgeRangeChange}
              valueLabelDisplay="auto"
              min={13}
              max={100}
              disabled={isParentManaged || isSavingPrivacy}
              marks={[
                { value: 13, label: '13' },
                { value: 18, label: '18' },
                { value: 30, label: '30' },
                { value: 50, label: '50' },
                { value: 70, label: '70' },
                { value: 100, label: '100' },
              ]}
              sx={{ mt: 2 }}
            />
          </Box>
        )}

        <Box mt={3}>
          <Button
            variant="contained"
            onClick={handleSavePrivacySettings}
            disabled={isParentManaged || isSavingPrivacy}
            startIcon={isSavingPrivacy ? <CircularProgress size={16} /> : null}
          >
            {isSavingPrivacy ? 'Saving...' : 'Save Restrictions'}
          </Button>
        </Box>
      </Paper>

      {/* Parental Controls */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, border: '1px solid', borderColor: 'divider' }}>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <SupervisedUserCircle sx={{ fontSize: 28, color: 'primary.main' }} />
          <Typography variant="h6">Parental Controls</Typography>
        </Box>

        {/* Managed Children Section */}
        {managedChildren.length > 0 && (
          <Box mb={3}>
            <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
              Managed Child Accounts
            </Typography>
            <List disablePadding>
              {managedChildren.map((child) => (
                <ListItem
                  key={child.childUserId}
                  sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1 }}
                >
                  <ListItemAvatar>
                    <Avatar src={child.childAvatarUrl || undefined}>
                      <ChildCare />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={child.childDisplayName || 'Child Account'}
                    secondary={`Linked ${new Date(child.linkedAt).toLocaleDateString()}`}
                  />
                  <ListItemSecondaryAction>
                    <Tooltip title="Manage Privacy Settings">
                      <IconButton onClick={() => handleOpenEditChild(child)}>
                        <Edit />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Unlink Child">
                      <IconButton
                        onClick={() => {
                          setUnlinkTarget({
                            linkId: child.linkId,
                            name: child.childDisplayName || 'this child',
                            type: 'child',
                          })
                          setShowUnlinkDialog(true)
                        }}
                      >
                        <Delete />
                      </IconButton>
                    </Tooltip>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        <Button
          variant="outlined"
          startIcon={<PersonAdd />}
          onClick={() => {
            setChildEmail('')
            setShowLinkChildDialog(true)
          }}
          sx={{ mb: 3 }}
        >
          Link Child Account
        </Button>

        {/* Parent Accounts Section */}
        {parentAccounts.length > 0 && (
          <Box mb={3}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
              Linked Parent Accounts
            </Typography>
            <List disablePadding>
              {parentAccounts.map((parent) => (
                <ListItem
                  key={parent.parentUserId}
                  sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1 }}
                >
                  <ListItemAvatar>
                    <Avatar src={parent.parentAvatarUrl || undefined} />
                  </ListItemAvatar>
                  <ListItemText
                    primary={parent.parentDisplayName || 'Parent Account'}
                    secondary={`Linked ${new Date(parent.linkedAt).toLocaleDateString()}`}
                  />
                  <ListItemSecondaryAction>
                    <Tooltip title="Unlink Parent">
                      <IconButton
                        onClick={() => {
                          setUnlinkTarget({
                            linkId: parent.linkId,
                            name: parent.parentDisplayName || 'this parent',
                            type: 'parent',
                          })
                          setShowUnlinkDialog(true)
                        }}
                      >
                        <Delete />
                      </IconButton>
                    </Tooltip>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
            <Typography variant="caption" color="text.secondary">
              Note: Children under 18 cannot unlink from parent accounts.
            </Typography>
          </Box>
        )}

        {/* Pending Inbound Requests Section (for children) */}
        {pendingRequests.length > 0 && (
          <Box>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
              Pending Link Requests
            </Typography>
            <List disablePadding>
              {pendingRequests.map((request) => (
                <ListItem
                  key={request.id}
                  sx={{ border: '1px solid', borderColor: 'warning.main', borderRadius: 1, mb: 1 }}
                >
                  <ListItemAvatar>
                    <Avatar />
                  </ListItemAvatar>
                  <ListItemText
                    primary={request.parentDisplayName || 'Parent'}
                    secondary={`Requested ${new Date(request.createdAt).toLocaleDateString()}`}
                  />
                  <ListItemSecondaryAction>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => setShowConfirmLinkDialog(true)}
                      sx={{ mr: 1 }}
                      disabled={isProcessingParental}
                    >
                      Confirm
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      color="error"
                      onClick={() => handleDeclineRequest(request.id, request.parentDisplayName || 'this parent')}
                      disabled={isProcessingParental}
                    >
                      Decline
                    </Button>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {/* Pending Outbound Requests Section (for parents) */}
        {pendingOutboundRequests.length > 0 && (
          <Box>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
              Pending Sent Requests
            </Typography>
            <List disablePadding>
              {pendingOutboundRequests.map((request) => (
                <ListItem
                  key={request.id}
                  sx={{ border: '1px solid', borderColor: 'info.main', borderRadius: 1, mb: 1 }}
                >
                  <ListItemAvatar>
                    <Avatar>
                      <ChildCare />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={request.childDisplayName || 'Child'}
                    secondary={
                      <>
                        Sent {new Date(request.createdAt).toLocaleDateString()}
                        {request.verificationCode && (
                          <> • Code: <strong>{request.verificationCode}</strong></>
                        )}
                      </>
                    }
                  />
                  <ListItemSecondaryAction>
                    {request.verificationCode && (
                      <Tooltip title="Copy verification code">
                        <IconButton
                          onClick={() => copyToClipboard(request.verificationCode!)}
                          sx={{ mr: 1 }}
                        >
                          <ContentCopy />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Cancel request">
                      <IconButton
                        onClick={() => handleCancelPendingRequest(request.id, request.childDisplayName || 'this child')}
                        disabled={isProcessingParental}
                        color="error"
                      >
                        <Cancel />
                      </IconButton>
                    </Tooltip>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </Paper>

      {/* MFA Setup Wizard Dialog */}
      <Dialog open={showSetupWizard} onClose={() => setShowSetupWizard(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Set Up Multi-Factor Authentication</DialogTitle>
        <DialogContent>
          <Stepper activeStep={activeStep} sx={{ mt: 2, mb: 4 }}>
            {setupSteps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {activeStep === 0 && (
            <Box>
              <Typography variant="body1" gutterBottom>
                Download an authenticator app on your phone:
              </Typography>
              <List>
                <ListItem>• Google Authenticator</ListItem>
                <ListItem>• Microsoft Authenticator</ListItem>
                <ListItem>• Authy</ListItem>
                <ListItem>• Any TOTP-compatible app</ListItem>
              </List>
            </Box>
          )}

          {activeStep === 1 && setupData && (
            <Box textAlign="center">
              <Typography variant="body1" gutterBottom>
                Scan this QR code with your authenticator app:
              </Typography>
              <Box my={3} display="flex" justifyContent="center">
                {setupData.qrCodeUrl && <ThemedQRCode data={setupData.qrCodeUrl} size={280} />}
              </Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Or enter this code manually:
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontFamily: 'monospace',
                  backgroundColor: 'background.default',
                  p: 1,
                  borderRadius: 1,
                }}
              >
                {setupData.manualEntryCode || setupData.secret}
              </Typography>
            </Box>
          )}

          {activeStep === 2 && (
            <Box>
              <Typography variant="body1" gutterBottom>
                Enter the 6-digit code from your authenticator app:
              </Typography>
              <TextField
                fullWidth
                label="Verification Code"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                inputProps={{
                  maxLength: 6,
                  style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5rem' },
                }}
                sx={{ mt: 2 }}
              />
            </Box>
          )}

          {activeStep === 3 && backupCodes && backupCodes.backupCodes && (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Save these backup codes in a secure location. Each code can only be used once.
              </Alert>
              <Card variant="outlined">
                <CardContent>
                  <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1}>
                    {backupCodes.backupCodes.map((code, index) => (
                      <Typography key={index} variant="body2" sx={{ fontFamily: 'monospace', textAlign: 'center' }}>
                        {code}
                      </Typography>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {activeStep > 0 && activeStep < 3 && (
            <Button onClick={handleBack} disabled={isSettingUp}>
              Back
            </Button>
          )}
          {activeStep < 2 && (
            <Button onClick={handleNext} variant="contained">
              Next
            </Button>
          )}
          {activeStep === 2 && (
            <Button
              onClick={handleVerifyAndEnable}
              variant="contained"
              disabled={verificationCode.length !== 6 || isSettingUp}
              startIcon={isSettingUp ? <CircularProgress size={16} /> : null}
            >
              {isSettingUp ? 'Verifying...' : 'Verify & Enable'}
            </Button>
          )}
          {activeStep === 3 && (
            <Button onClick={handleFinishSetup} variant="contained">
              Finish
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Disable MFA Dialog */}
      <Dialog open={showDisableDialog} onClose={() => setShowDisableDialog(false)}>
        <DialogTitle>Disable Multi-Factor Authentication</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Disabling 2FA will make your account less secure.
          </Alert>
          <Typography variant="body2" gutterBottom>
            Enter a verification code from your authenticator app to confirm:
          </Typography>
          <TextField
            fullWidth
            label="Verification Code"
            value={disableCode}
            onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="000000"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDisableDialog(false)}>Cancel</Button>
          <Button
            onClick={handleDisableMfa}
            color="error"
            variant="contained"
            disabled={disableCode.length !== 6 || isSettingUp}
          >
            Disable 2FA
          </Button>
        </DialogActions>
      </Dialog>

      {/* Backup Codes Dialog */}
      <Dialog open={showBackupCodes} onClose={() => setShowBackupCodes(false)}>
        <DialogTitle>Backup Codes</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            These codes replace your previous backup codes. Save them in a secure location.
          </Alert>
          {backupCodes && backupCodes.backupCodes && (
            <Card variant="outlined">
              <CardContent>
                <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1}>
                  {backupCodes.backupCodes.map((code, index) => (
                    <Typography key={index} variant="body2" sx={{ fontFamily: 'monospace', textAlign: 'center' }}>
                      {code}
                    </Typography>
                  ))}
                </Box>
              </CardContent>
            </Card>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowBackupCodes(false)} variant="contained">
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* Link Child Dialog */}
      <Dialog open={showLinkChildDialog} onClose={() => setShowLinkChildDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Link Child Account</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom sx={{ mt: 1 }}>
            Enter the email address or username of the child account you want to link:
          </Typography>
          <TextField
            fullWidth
            label="Child's Email or Username"
            value={childEmail}
            onChange={(e) => setChildEmail(e.target.value)}
            sx={{ mt: 2 }}
            disabled={isProcessingParental}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            A link request will be sent to the child account. They will need to confirm it in their settings.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowLinkChildDialog(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleLinkChild}
            variant="contained"
            disabled={!childEmail || isProcessingParental}
            startIcon={isProcessingParental ? <CircularProgress size={16} /> : null}
          >
            {isProcessingParental ? 'Sending...' : 'Send Request'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Link Dialog */}
      <Dialog open={showConfirmLinkDialog} onClose={() => setShowConfirmLinkDialog(false)}>
        <DialogTitle>Confirm Parental Link</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom sx={{ mt: 1 }}>
            Enter the verification code provided by your parent:
          </Typography>
          <TextField
            fullWidth
            label="Verification Code"
            value={linkVerificationCode}
            onChange={(e) => setLinkVerificationCode(e.target.value.toUpperCase())}
            sx={{ mt: 2 }}
            disabled={isProcessingParental}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmLinkDialog(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmLink}
            variant="contained"
            disabled={!linkVerificationCode || isProcessingParental}
            startIcon={isProcessingParental ? <CircularProgress size={16} /> : null}
          >
            {isProcessingParental ? 'Confirming...' : 'Confirm Link'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Child Settings Dialog */}
      <Dialog open={showEditChildDialog} onClose={() => setShowEditChildDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Manage {selectedChild?.childDisplayName || 'Child'}'s Privacy Settings</DialogTitle>
        <DialogContent>
          <FormControlLabel
            control={
              <Switch
                checked={editChildAgeEnabled}
                onChange={(e) => setEditChildAgeEnabled(e.target.checked)}
                disabled={isProcessingParental}
              />
            }
            label="Restrict who can start chats based on age"
            sx={{ mt: 1 }}
          />

          {editChildAgeEnabled && (
            <Box mt={3} px={2}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Allow chats from users aged {editChildAgeRange[0]} to {editChildAgeRange[1]} years
              </Typography>
              <Slider
                value={editChildAgeRange}
                onChange={handleEditChildAgeRangeChange}
                valueLabelDisplay="auto"
                min={13}
                max={100}
                disabled={isProcessingParental}
                marks={[
                  { value: 13, label: '13' },
                  { value: 18, label: '18' },
                  { value: 30, label: '30' },
                  { value: 50, label: '50' },
                  { value: 70, label: '70' },
                  { value: 100, label: '100' },
                ]}
                sx={{ mt: 2 }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowEditChildDialog(false)}>Cancel</Button>
          <Button
            onClick={handleSaveChildSettings}
            variant="contained"
            disabled={isProcessingParental}
            startIcon={isProcessingParental ? <CircularProgress size={16} /> : null}
          >
            {isProcessingParental ? 'Saving...' : 'Save Settings'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unlink Confirmation Dialog */}
      <Dialog open={showUnlinkDialog} onClose={() => setShowUnlinkDialog(false)}>
        <DialogTitle>Confirm Unlink</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to unlink {unlinkTarget?.name}? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowUnlinkDialog(false)}>Cancel</Button>
          <Button
            onClick={handleUnlink}
            color="error"
            variant="contained"
            disabled={isProcessingParental}
            startIcon={isProcessingParental ? <CircularProgress size={16} /> : null}
          >
            {isProcessingParental ? 'Unlinking...' : 'Unlink'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
